import { useState } from 'react'
import { Settings, User, Bell, Shield, Palette, Globe, Moon, Sun, Eye, EyeOff } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface PersonalSettingsProps {
  className?: string
}

export function PersonalSettings({ className }: PersonalSettingsProps) {
  const [settings, setSettings] = useState({
    // 基本信息
    nickname: '当前用户',
    bio: '这个人很懒，什么都没有留下...',
    avatar: '/placeholder.svg?height=80&width=80',
    school: '大悟一中',
    grade: '高三',
    
    // 隐私设置
    profileVisibility: 'public', // public, friends, private
    showOnlineStatus: true,
    allowMessages: true,
    showActivity: true,
    
    // 通知设置
    pushNotifications: true,
    emailNotifications: false,
    commentNotifications: true,
    likeNotifications: true,
    messageNotifications: true,
    systemNotifications: true,
    
    // 界面设置
    theme: 'light', // light, dark, auto
    language: 'zh-CN',
    fontSize: 14,
    compactMode: false,
    
    // 内容设置
    autoSave: true,
    showSensitiveContent: false,
    defaultCategory: 'all',
    postsPerPage: 20
  })

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
    toast.success('设置已保存')
  }

  const handleAvatarUpload = () => {
    toast.info('头像上传功能开发中...')
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* 基本信息设置 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 头像设置 */}
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20">
              <AvatarImage src={settings.avatar} />
              <AvatarFallback>{settings.nickname[0]}</AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <Button onClick={handleAvatarUpload} variant="outline">
                更换头像
              </Button>
              <p className="text-sm text-gray-500">
                支持 JPG、PNG 格式，建议尺寸 200x200px
              </p>
            </div>
          </div>

          {/* 昵称 */}
          <div className="space-y-2">
            <Label htmlFor="nickname">昵称</Label>
            <Input
              id="nickname"
              value={settings.nickname}
              onChange={(e) => handleSettingChange('nickname', e.target.value)}
              placeholder="请输入昵称"
            />
          </div>

          {/* 个人简介 */}
          <div className="space-y-2">
            <Label htmlFor="bio">个人简介</Label>
            <Textarea
              id="bio"
              value={settings.bio}
              onChange={(e) => handleSettingChange('bio', e.target.value)}
              placeholder="介绍一下自己吧..."
              className="min-h-[80px]"
            />
          </div>

          {/* 学校和年级 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="school">学校</Label>
              <Select value={settings.school} onValueChange={(value) => handleSettingChange('school', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="大悟一中">大悟一中</SelectItem>
                  <SelectItem value="楚才高中">楚才高中</SelectItem>
                  <SelectItem value="城关中学">城关中学</SelectItem>
                  <SelectItem value="行健高中">行健高中</SelectItem>
                  <SelectItem value="思源学校">思源学校</SelectItem>
                  <SelectItem value="天有学校">天有学校</SelectItem>
                  <SelectItem value="实验中学">实验中学</SelectItem>
                  <SelectItem value="其他">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="grade">年级</Label>
              <Select value={settings.grade} onValueChange={(value) => handleSettingChange('grade', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="初一">初一</SelectItem>
                  <SelectItem value="初二">初二</SelectItem>
                  <SelectItem value="初三">初三</SelectItem>
                  <SelectItem value="高一">高一</SelectItem>
                  <SelectItem value="高二">高二</SelectItem>
                  <SelectItem value="高三">高三</SelectItem>
                  <SelectItem value="大学">大学</SelectItem>
                  <SelectItem value="其他">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 隐私设置 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            隐私设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>个人资料可见性</Label>
              <p className="text-sm text-gray-500">控制谁可以查看你的个人资料</p>
            </div>
            <Select value={settings.profileVisibility} onValueChange={(value) => handleSettingChange('profileVisibility', value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">公开</SelectItem>
                <SelectItem value="friends">仅好友</SelectItem>
                <SelectItem value="private">私密</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>显示在线状态</Label>
              <p className="text-sm text-gray-500">让其他用户看到你的在线状态</p>
            </div>
            <Switch
              checked={settings.showOnlineStatus}
              onCheckedChange={(checked) => handleSettingChange('showOnlineStatus', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>允许私信</Label>
              <p className="text-sm text-gray-500">允许其他用户给你发送私信</p>
            </div>
            <Switch
              checked={settings.allowMessages}
              onCheckedChange={(checked) => handleSettingChange('allowMessages', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>显示活动状态</Label>
              <p className="text-sm text-gray-500">在动态中显示你的活动</p>
            </div>
            <Switch
              checked={settings.showActivity}
              onCheckedChange={(checked) => handleSettingChange('showActivity', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* 通知设置 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            通知设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>推送通知</Label>
              <p className="text-sm text-gray-500">接收浏览器推送通知</p>
            </div>
            <Switch
              checked={settings.pushNotifications}
              onCheckedChange={(checked) => handleSettingChange('pushNotifications', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>邮件通知</Label>
              <p className="text-sm text-gray-500">接收重要消息的邮件提醒</p>
            </div>
            <Switch
              checked={settings.emailNotifications}
              onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <Label className="text-base font-medium">通知类型</Label>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>评论通知</Label>
                <Switch
                  checked={settings.commentNotifications}
                  onCheckedChange={(checked) => handleSettingChange('commentNotifications', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>点赞通知</Label>
                <Switch
                  checked={settings.likeNotifications}
                  onCheckedChange={(checked) => handleSettingChange('likeNotifications', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>私信通知</Label>
                <Switch
                  checked={settings.messageNotifications}
                  onCheckedChange={(checked) => handleSettingChange('messageNotifications', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>系统通知</Label>
                <Switch
                  checked={settings.systemNotifications}
                  onCheckedChange={(checked) => handleSettingChange('systemNotifications', checked)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 界面设置 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            界面设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>主题模式</Label>
              <p className="text-sm text-gray-500">选择你喜欢的界面主题</p>
            </div>
            <Select value={settings.theme} onValueChange={(value) => handleSettingChange('theme', value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">
                  <div className="flex items-center gap-2">
                    <Sun className="w-4 h-4" />
                    浅色
                  </div>
                </SelectItem>
                <SelectItem value="dark">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    深色
                  </div>
                </SelectItem>
                <SelectItem value="auto">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    自动
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>字体大小: {settings.fontSize}px</Label>
              <Slider
                value={[settings.fontSize]}
                onValueChange={([value]) => handleSettingChange('fontSize', value)}
                min={12}
                max={18}
                step={1}
                className="w-full"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>紧凑模式</Label>
                <p className="text-sm text-gray-500">减少界面元素间距</p>
              </div>
              <Switch
                checked={settings.compactMode}
                onCheckedChange={(checked) => handleSettingChange('compactMode', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 内容设置 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            内容设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>自动保存草稿</Label>
              <p className="text-sm text-gray-500">编辑时自动保存内容</p>
            </div>
            <Switch
              checked={settings.autoSave}
              onCheckedChange={(checked) => handleSettingChange('autoSave', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>显示敏感内容</Label>
              <p className="text-sm text-gray-500">显示可能包含敏感信息的内容</p>
            </div>
            <Switch
              checked={settings.showSensitiveContent}
              onCheckedChange={(checked) => handleSettingChange('showSensitiveContent', checked)}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>默认分类</Label>
              <p className="text-sm text-gray-500">发布内容时的默认分类</p>
            </div>
            <Select value={settings.defaultCategory} onValueChange={(value) => handleSettingChange('defaultCategory', value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="表白墙">表白墙</SelectItem>
                <SelectItem value="失物招领">失物招领</SelectItem>
                <SelectItem value="二手交易">二手交易</SelectItem>
                <SelectItem value="学习交流">学习交流</SelectItem>
                <SelectItem value="校园生活">校园生活</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>每页显示条数: {settings.postsPerPage}</Label>
            <Slider
              value={[settings.postsPerPage]}
              onValueChange={([value]) => handleSettingChange('postsPerPage', value)}
              min={10}
              max={50}
              step={5}
              className="w-full"
            />
          </div>
        </CardContent>
      </Card>

      {/* 保存按钮 */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => toast.info('设置已重置')}>
          重置设置
        </Button>
        <Button onClick={() => toast.success('所有设置已保存')}>
          保存设置
        </Button>
      </div>
    </div>
  )
}